import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import localeText from "../../../../../locale";
import UserStoreAtecoSection from "../../../components/UserStore/components/UserStoreAtecoSection/UserStoreAtecoSection";
import StoreSettingsFieldRow from "./StoreSettingsFieldRow";
import { normalizeStoreSlug, STORE_SLUG_MAX_LENGTH } from "../../../../../utilities/storeSlug";

const MAX_LOGO_SIZE_BYTES = 5242880;

const StoreSettingsGeneralTab = ({
  logoDisplayUrl,
  onLogoFileChange,
  onLogoInputRef,
  title,
  setTitle,
  description,
  setDescription,
  slug,
  setSlug,
  savedSlug,
  selectedCategories,
  setSelectedCategories,
  onAddCategory,
  onRemoveCategory,
  categoriesOptions,
  loadingCategories,
  onSave,
  saving,
  showErrorToast,
}) => {
  const logoInputRef = useRef(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryToAdd, setCategoryToAdd] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!linkCopied) return undefined;
    const timer = setTimeout(() => setLinkCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [linkCopied]);

  // Copia o link do slug já salvo (o que está no campo pode ainda não existir)
  const handleCopyStoreLink = async () => {
    if (!savedSlug) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${savedSlug}`);
      setLinkCopied(true);
    } catch (e) {
      showErrorToast({ code: 500000 });
    }
  };

  useEffect(() => {
    const options = Array.isArray(categoriesOptions) ? categoriesOptions : [];
    const selectedIds = new Set((selectedCategories || []).map((cat) => cat?.id));

    setAvailableCategories(options.filter((opt) => !selectedIds.has(opt?.value?.id)));
  }, [categoriesOptions, selectedCategories]);

  const refCallback = (el) => {
    logoInputRef.current = el;
    if (onLogoInputRef) onLogoInputRef(el);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      showErrorToast({ detail: "fileSizeTooBig" });
      e.target.value = "";
      return;
    }
    onLogoFileChange(file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-column gap-2">
      <StoreSettingsFieldRow label={localeText("logo")} help={localeText("storeSettingsLogoHelp")}>
        <div className="store-settings-asset-row">
          {logoDisplayUrl && (
            <div className="store-settings-logo-preview">
              <img src={logoDisplayUrl} alt={localeText("logo")} />
            </div>
          )}
          <div
            className="store-settings-fileupload p-fileupload p-component"
            style={{ flex: 1, minWidth: "200px" }}
          >
            <div className="p-fileupload-content">
              <input
                ref={refCallback}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoChange}
                aria-label={localeText("logo")}
              />
              <div className="store-settings-fileupload-empty">
                <button
                  type="button"
                  className="store-settings-fileupload-icon-btn"
                  onClick={() => logoInputRef.current?.click()}
                  aria-label={localeText("storeSettingsClickToUpload")}
                >
                  <i className="pi pi-cloud-upload" />
                </button>
                <p className="store-settings-fileupload-primary">
                  <span className="store-settings-upload-highlight">
                    {localeText("storeSettingsClickToUpload")}
                  </span>
                  <span className="store-settings-upload-muted">
                    {" "}
                    {localeText("storeSettingsOrDragDrop")}
                  </span>
                </p>
                <p className="store-settings-fileupload-secondary">
                  {localeText("storeSettingsLogoFormats")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </StoreSettingsFieldRow>
      <hr
        className="my-4"
        style={{ borderColor: "color-mix(in srgb, var(--theme-border) 50%, transparent)" }}
      />
      <StoreSettingsFieldRow
        label={localeText("title")}
        help={localeText("storeSettingsTitleHelp")}
      >
        <InputText
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={localeText("title")}
          className="w-full border-round-md"
        />
      </StoreSettingsFieldRow>
      <StoreSettingsFieldRow
        label={localeText("slug")}
        help={localeText("storeSettingsStoreLinkHelp")}
      >
        <div className="p-inputgroup border-round-md">
          <span className="p-inputgroup-addon" style={{ color: "var(--theme-text-muted)" }}>
            {`${window.location.host}/`}
          </span>
          <InputText
            value={slug ?? ""}
            onChange={(e) => setSlug(normalizeStoreSlug(e.target.value))}
            maxLength={STORE_SLUG_MAX_LENGTH}
            placeholder={localeText("storeSettingsStoreLinkPlaceholder")}
            aria-label={localeText("slug")}
          />
          <Button
            type="button"
            icon={linkCopied ? "pi pi-check" : "pi pi-copy"}
            label={localeText(linkCopied ? "storeSettingsLinkCopied" : "storeSettingsCopyLink")}
            className="p-button-outlined"
            onClick={handleCopyStoreLink}
            disabled={!savedSlug}
            tooltip={!savedSlug ? localeText("storeSettingsCopyLinkDisabled") : undefined}
            tooltipOptions={{ showOnDisabled: true, position: "top" }}
          />
        </div>
      </StoreSettingsFieldRow>
      <hr
        className="my-4"
        style={{ borderColor: "color-mix(in srgb, var(--theme-border) 50%, transparent)" }}
      />
      <StoreSettingsFieldRow
        label={localeText("description")}
        help={localeText("storeSettingsDescriptionHelp")}
      >
        <InputTextarea
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={localeText("description")}
          className="w-full border-round-md"
        />
      </StoreSettingsFieldRow>
      <hr
        className="my-4"
        style={{ borderColor: "color-mix(in srgb, var(--theme-border) 50%, transparent)" }}
      />
      <StoreSettingsFieldRow
        label={localeText("addAteco")}
        help={localeText("storeSettingsAtecoHelp")}
      >
        <UserStoreAtecoSection hideTitle />
      </StoreSettingsFieldRow>
      <hr
        className="my-4"
        style={{ borderColor: "color-mix(in srgb, var(--theme-border) 50%, transparent)" }}
      />
      {!loadingCategories && (
        <StoreSettingsFieldRow
          label={localeText("newProductcategories")}
          help={localeText("storeSettingsCategoriesHelp")}
        >
          {selectedCategories.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {selectedCategories.map((cat) => (
                <span
                  key={cat.id}
                  className="p-tag flex items-center gap-1 align-items-center"
                  style={{ backgroundColor: "var(--theme-primary)", color: "#fff" }}
                >
                  {cat.label}

                  <i
                    onClick={() => {
                      if (onRemoveCategory) {
                        onRemoveCategory(cat);
                        return;
                      }

                      setSelectedCategories((prev) => prev.filter((c) => c.id !== cat.id));
                    }}
                    className="pi pi-times text-xs cursor-pointer"
                  ></i>
                </span>
              ))}
            </div>
          )}

          <Dropdown
            value={categoryToAdd}
            options={availableCategories}
            onChange={(e) => {
              const selected = e.value;
              setCategoryToAdd(selected);

              if (!selected?.id) return;

              setSelectedCategories((prev) => {
                const exists = prev.some((cat) => cat?.id === selected.id);
                return exists ? prev : [...prev, selected];
              });

              if (onAddCategory) onAddCategory(selected);

              setCategoryToAdd(null);
            }}
            optionLabel="label"
            optionValue="value"
            placeholder={localeText("newProductcategories")}
            className="w-full border-round-md"
          />
        </StoreSettingsFieldRow>
      )}
      <hr
        className="my-4"
        style={{ borderColor: "color-mix(in srgb, var(--theme-border) 50%, transparent)" }}
      />
      <div className="flex justify-content-end">
        <Button
          label={localeText("save")}
          onClick={onSave}
          loading={saving}
          className="w-auto border-round-md"
          style={{ backgroundColor: "var(--theme-primary)", color: "#fff", border: "none" }}
        />
      </div>
    </div>
  );
};

export default StoreSettingsGeneralTab;
